import { createSlice } from '@reduxjs/toolkit';
import {
  fetchWaterChangeStandard,
  saveWaterChangeStandard,
} from '../actions/waterChangeStandardThunks';

/**
 * waterChangeStandardSlice
 *
 * Manages fetching and saving the water change standard for a facility.
 *
 * State:
 *   - facilityId: ID of the selected facility
 *   - initialStandard: value loaded from the backend
 *   - currentStandard: user’s unsaved selection
 *   - isFetching, isSaving: loading flags
 *   - hasChanges: true if currentStandard ≠ initialStandard
 *   - error: last error message, if any
 */
const initialState = {
  facilityId: null,
  initialStandard: '',
  currentStandard: '',
  isFetching: false,
  isSaving: false,
  hasChanges: false,
  error: null,
};

const waterChangeStandardSlice = createSlice({
  name: 'waterChangeStandard',
  initialState,
  reducers: {
    setFacilityId(state, action) {
      state.facilityId = action.payload;
    },
    updateCurrentStandard(state, action) {
      state.currentStandard = action.payload;
      state.hasChanges = state.currentStandard !== state.initialStandard;
    },
  },
  extraReducers(builder) {
    builder
      // FETCH
      .addCase(fetchWaterChangeStandard.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(fetchWaterChangeStandard.fulfilled, (state, { payload }) => {
        state.isFetching = false;
        state.initialStandard = payload.standard;
        if (!state.hasChanges) {
          state.currentStandard = payload.standard;
          state.hasChanges = false;
        }
      })
      .addCase(fetchWaterChangeStandard.rejected, (state, { payload }) => {
        state.isFetching = false;
        state.error = payload;
      })

      // SAVE
      .addCase(saveWaterChangeStandard.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveWaterChangeStandard.fulfilled, (state) => {
        state.isSaving = false;
        state.initialStandard = state.currentStandard;
        state.hasChanges = false;
      })
      .addCase(saveWaterChangeStandard.rejected, (state, { payload }) => {
        state.isSaving = false;
        state.error = payload;
      });
  },
});

export const { setFacilityId, updateCurrentStandard } = waterChangeStandardSlice.actions;
export default waterChangeStandardSlice.reducer;
